import React from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SecurityIcon from "@mui/icons-material/Security";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

const PrivacyPage = () => {
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          component={RouterLink}
          to="/"
          sx={{
            display: "flex",
            alignItems: "center",
            color: "text.secondary",
            textDecoration: "none",
          }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Typography color="text.primary">Privacy Policy</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <SecurityIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Privacy Policy
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Last Updated:{" "}
          {new Date().toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </Typography>
      </Box>

      {/* HIPAA Compliance Alert */}
      <Alert severity="success" icon={<SecurityIcon />} sx={{ mb: 3 }}>
        <strong>HIPAA Compliant:</strong> NephroTrax is fully compliant with
        HIPAA regulations and takes the security of your protected health
        information (PHI) very seriously.
      </Alert>

      {/* Content */}
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Introduction */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Introduction
        </Typography>
        <Typography variant="body1" paragraph>
          NephroTrax ("we," "our," or "us") is committed to protecting your
          privacy and ensuring the security of your personal and health
          information. This Privacy Policy explains how we collect, use,
          disclose, and safeguard your information when you use our platform.
        </Typography>
        <Typography variant="body1" paragraph>
          By using NephroTrax, you agree to the collection and use of
          information in accordance with this policy. If you do not agree with
          our policies and practices, please do not use our services.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 1. Information We Collect */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          1. Information We Collect
        </Typography>

        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
          1.1 Personal Information
        </Typography>
        <Typography variant="body1" paragraph>
          We collect information that you provide directly to us, including:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Account Information"
              secondary="Name, email address, date of birth, gender, phone number, and mailing address"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Profile Information"
              secondary="User preferences, profile photo, and communication preferences"
            />
          </ListItem>
        </List>

        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
          1.2 Protected Health Information (PHI)
        </Typography>
        <Typography variant="body1" paragraph>
          With your explicit consent, we collect health-related information
          including:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Medical History"
              secondary="Amyloidosis type, diagnosis date, disease stage, and treatment history"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Organ Involvement"
              secondary="Information about affected organs and symptoms"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Medications"
              secondary="Current and past medications, dosages, and treatment responses"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Lab Results"
              secondary="Biomarker values such as NT-proBNP, troponin, free light chains, and kidney function"
            />
          </ListItem>
        </List>

        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
          1.3 Usage Information
        </Typography>
        <Typography variant="body1" paragraph>
          We automatically collect certain information about your device and how
          you interact with our platform:
        </Typography>
        <List>
          <ListItem>
            <ListItemText secondary="Log data (IP address, browser type, pages visited, time spent)" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Device information (device type, operating system, unique device identifiers)" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Cookies and similar tracking technologies" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 2. How We Use Your Information */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          2. How We Use Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We use the information we collect for the following purposes:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Clinical Trial Matching"
              secondary="To match you with relevant clinical trials based on your medical profile and preferences"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Service Delivery"
              secondary="To provide, maintain, and improve our platform and services"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Communication"
              secondary="To send you updates, trial alerts, educational content, and respond to your inquiries"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Research & Analytics"
              secondary="To conduct research, analyze trends, and improve treatment outcomes (only with de-identified data)"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Security & Compliance"
              secondary="To protect against fraud, ensure platform security, and comply with legal obligations"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 3. How We Share Your Information */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          3. How We Share Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We do not sell your personal information. We may share your
          information in the following circumstances:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="With Your Consent"
              secondary="We share your information with clinical trial sites and researchers only when you explicitly authorize us to do so"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Healthcare Providers"
              secondary="With your healthcare providers to coordinate care and clinical trial participation"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Service Providers"
              secondary="With third-party vendors who perform services on our behalf (cloud hosting, analytics, email delivery) under strict confidentiality agreements"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Third-Party AI Service (Care Copilot)"
              secondary='When you use Care Copilot, your messages and any health information you enter in that conversation are sent to Claude, an AI service by Anthropic, delivered securely through AWS Bedrock under a signed Business Associate Agreement (BAA). You are shown an explicit disclosure and must provide consent before this data is sent, the first time you use Care Copilot.'
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Research Partners"
              secondary="De-identified data may be shared with research institutions to advance amyloidosis research"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Legal Requirements"
              secondary="When required by law, court order, or government regulation"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 4. Data Security */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          4. Data Security
        </Typography>
        <Typography variant="body1" paragraph>
          We implement industry-standard security measures to protect your
          information:
        </Typography>
        <List>
          <ListItem>
            <ListItemText secondary="End-to-end encryption for data transmission (SSL/TLS)" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Encryption at rest for stored data (AES-256)" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Regular security audits and penetration testing" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Access controls and authentication (Auth0)" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="HIPAA-compliant infrastructure (AWS)" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Employee training on data privacy and security" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 5. Your Rights and Choices */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          5. Your Rights and Choices
        </Typography>
        <Typography variant="body1" paragraph>
          You have the following rights regarding your information:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Access & Download"
              secondary="Request a copy of your personal and health information"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Correction"
              secondary="Update or correct inaccurate information in your profile"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Deletion"
              secondary="Request deletion of your account and associated data (subject to legal retention requirements)"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Opt-Out"
              secondary="Unsubscribe from marketing communications while retaining your account"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Consent Withdrawal"
              secondary="Revoke consent for research participation or data sharing at any time"
            />
          </ListItem>
        </List>

        <Alert severity="info" sx={{ mt: 2 }}>
          To exercise these rights, please contact us at{" "}
          <strong>reviewer@verainsight.com</strong> or through your account
          settings.
        </Alert>

        <Divider sx={{ my: 3 }} />

        {/* 6. Data Retention */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          6. Data Retention
        </Typography>
        <Typography variant="body1" paragraph>
          We retain your information for as long as your account is active or as
          needed to provide services. After account deletion, we may retain
          certain information for:
        </Typography>
        <List>
          <ListItem>
            <ListItemText secondary="Legal compliance (7 years for health records)" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Dispute resolution and fraud prevention" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="De-identified research purposes (indefinitely)" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 7. Children's Privacy */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          7. Children's Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          NephroTrax is not intended for children under 18 years of age. We
          do not knowingly collect personal information from children. If you
          believe we have collected information from a child, please contact us
          immediately.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 8. International Users */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          8. International Users
        </Typography>
        <Typography variant="body1" paragraph>
          Our services are hosted in the United States. If you access our
          platform from outside the U.S., your information will be transferred
          to, stored, and processed in the United States. By using our services,
          you consent to this transfer.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 9. Changes to This Policy */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          9. Changes to This Privacy Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We may update this Privacy Policy from time to time. We will notify
          you of any material changes by:
        </Typography>
        <List>
          <ListItem>
            <ListItemText secondary="Posting the new policy on this page with an updated 'Last Updated' date" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Sending an email notification to your registered email address" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Displaying a prominent notice on our platform" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 10. Contact Us */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          10. Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have questions or concerns about this Privacy Policy or our
          data practices, please contact us:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Email"
              secondary="sandeep.sinha@verainsight.com"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Mail"
              secondary={
                <>
                  VeraInsight Healthcare Inc.
                  <br />
                  Chicago, IL
                </>
              }
            />
          </ListItem>
        </List>
      </Paper>

      {/* Related Links */}
      <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}>
        <Link
          component={RouterLink}
          to="/terms"
          sx={{ textDecoration: "none" }}
        >
          Terms of Service
        </Link>
        <Typography color="text.secondary">•</Typography>
        <Link
          component={RouterLink}
          to="/hipaa"
          sx={{ textDecoration: "none" }}
        >
          HIPAA Compliance
        </Link>
        <Typography color="text.secondary">•</Typography>
        <Link
          component={RouterLink}
          to="/contact"
          sx={{ textDecoration: "none" }}
        >
          Contact Us
        </Link>
      </Box>
    </Container>
  );
};

export default PrivacyPage;